import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { exportEtudiants, fetchAllEtudiantsForExport } from '../utils/exportUtils';
import CreateEtudiantAccountModal from '../components/CreateEtudiantAccountModal';
import {
  EtudiantPageHeader,
  EtudiantFilters,
  EtudiantTable,
  EtudiantEditModal
} from '../components/etudiant';
import api from '../components/api';

// Custom hook pour gérer les données d'étudiant
const useEtudiantData = (currentPage, pageSize, selectedClasse, selectedNiveau, selectedFiliere, selectedAnnee, searchTerm) => {
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [erreur, setErreur] = useState(null);
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);

  // Fonction pour récupérer les données
  const fetchData = async () => {
    setLoading(true);
    setErreur(null);
    try {
      // Récupérer la liste des étudiants avec pagination et filtres
      const response = await api.get('/etudiant/liste-etudiants/', {
        params: {
          page: currentPage,
          page_size: pageSize,
          classe: selectedClasse || '',
          niveau: selectedNiveau || '',
          filiere: selectedFiliere || '',
          annee: selectedAnnee || '',
          search: searchTerm || ''
        }
      });

      // Traiter la réponse même si elle est vide
      if (response.data) {
        if (response.data.data) {
          setEtudiants(response.data.data || []);
          setTotalPages(response.data.total_pages || 0);
          setTotalItems(response.data.count || 0);
        } else {
          // Si pas de données mais pas d'erreur 404, c'est probablement vide
          setEtudiants([]);
          setTotalPages(0);
          setTotalItems(0);
        }
      } else {
        setEtudiants([]);
        setTotalPages(0);
        setTotalItems(0);
      }

      // Récupérer les autres données
      const [
        filieresResponse, 
        niveauxResponse, 
        classesResponse,
        anneesResponse
      ] = await Promise.all([
        api.get('/filiere/liste-filieres/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/classe/classe-list-create/'),
        api.get('/etablissement/annees-academiques/')
      ]);

      setFilieres(filieresResponse.data.results || filieresResponse.data || []);
      setNiveaux(niveauxResponse.data.results || niveauxResponse.data || []);
      setClasses(classesResponse.data.results || classesResponse.data || []);
      setAnneesAcademiques(anneesResponse.data || []);
      
    } catch (error) {
      console.error('Erreur de chargement des données:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setEtudiants([]);
        setTotalPages(0);
        setTotalItems(0);
        setErreur(null); // Pas d'erreur si c'est juste vide
      } else {
        setErreur(error.message || 'Erreur de chargement des données');
        toast.error('Erreur de chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au changement de page, taille ou filtres
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedClasse, selectedNiveau, selectedFiliere, selectedAnnee, searchTerm]);

  return {
    etudiants,
    loading,
    totalPages,
    totalItems,
    erreur,
    fetchData,
    classes,
    niveaux,
    filieres,
    anneesAcademiques
  };
};

const EtudiantPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [selectedFiliere, setSelectedFiliere] = useState('');
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  
  // États pour les modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEtudiant, setSelectedEtudiant] = useState(null);
  const [etudiantForAccount, setEtudiantForAccount] = useState(null);
  
  // Utiliser le custom hook avec tous les filtres
  const {
    etudiants,
    loading,
    totalPages,
    totalItems,
    erreur,
    fetchData,
    classes,
    niveaux,
    filieres,
    anneesAcademiques
  } = useEtudiantData(
    currentPage, 
    pageSize, 
    selectedClasse,
    selectedNiveau,
    selectedFiliere,
    selectedAnnee,
    searchTerm
  );

  // Gestionnaires d'événements
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClasseChange = (e) => {
    setSelectedClasse(e.target.value);
    setCurrentPage(1);
  };

  const handleNiveauChange = (e) => {
    setSelectedNiveau(e.target.value);
    setCurrentPage(1);
  };

  const handleFiliereChange = (e) => {
    setSelectedFiliere(e.target.value);
    setCurrentPage(1);
  };

  const handleAnneeChange = (e) => {
    setSelectedAnnee(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (etudiant) => {
    setSelectedEtudiant(etudiant);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (etudiant) => {
    try {
      await api.put(`etudiant/details-etudiants/${etudiant.id}/`, etudiant);
      toast.success('Étudiant modifié avec succès');
      setIsEditModalOpen(false);
      setSelectedEtudiant(null);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la modification de l\'étudiant:', error);
      toast.error('Erreur lors de la modification de l\'étudiant');
    }
  };

  const handleCreateAccount = (etudiant) => {
    setEtudiantForAccount(etudiant);
  };

  // Fonction pour gérer les exports
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `etudiants_${timestamp}`;
      
      // Préparer les filtres actuels pour récupérer toutes les données
      const filters = {
        classe: selectedClasse || '',
        niveau: selectedNiveau || '',
        filiere: selectedFiliere || '',
        annee: selectedAnnee || '',
        search: searchTerm || ''
      };
      
      // Récupérer toutes les données avec les filtres
      const allEtudiants = await fetchAllEtudiantsForExport(filters);
      
      if (!allEtudiants || allEtudiants.length === 0) {
        toast.warning('Aucune donnée à exporter');
        return;
      }
      
      // Exporter avec les données récupérées
      const result = await exportEtudiants(allEtudiants, format, filename, filters);
      
      if (result.success) {
        toast.success(result.message || `Export ${format.toUpperCase()} terminé avec succès`);
      } else {
        toast.error(result.message || 'Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      toast.error('Erreur lors de l\'export des données: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            
            {/* Header avec bannière bleue */}
            <EtudiantPageHeader
              onRefresh={fetchData}
              onExport={handleExport}
              isExporting={isExporting}
            />
            
            {/* Filtres */}
            <EtudiantFilters
              searchTerm={searchTerm}
              onSearchChange={handleSearch}
              selectedClasse={selectedClasse}
              onClasseChange={handleClasseChange}
              selectedNiveau={selectedNiveau}
              onNiveauChange={handleNiveauChange}
              selectedFiliere={selectedFiliere}
              onFiliereChange={handleFiliereChange}
              selectedAnnee={selectedAnnee}
              onAnneeChange={handleAnneeChange}
              classes={classes}
              niveaux={niveaux}
              filieres={filieres}
              anneesAcademiques={anneesAcademiques}
            />

            {/* Tableau principal */}
            <EtudiantTable
              etudiants={etudiants}
              loading={loading}
              erreur={erreur}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              searchTerm={searchTerm}
              selectedClasse={selectedClasse}
              selectedNiveau={selectedNiveau}
              selectedFiliere={selectedFiliere}
              onPageChange={handlePageChange}
              onEdit={handleEdit}
              onCreateAccount={handleCreateAccount}
            />
          </div>
        </div>
      </div>

      {/* Modal de modification */}
      <EtudiantEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEtudiant(null);
        }}
        etudiant={selectedEtudiant}
        onSubmit={handleEditSubmit}
      />

      {/* Modal de création de compte étudiant */}
      <CreateEtudiantAccountModal
        isOpen={etudiantForAccount !== null}
        onClose={() => setEtudiantForAccount(null)}
        etudiant={etudiantForAccount}
        onSuccess={() => {
          // Optionnel: rafraîchir les données si nécessaire
        }}
      />
    </Layout>
  );
};

export default EtudiantPage;
